import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Public Pages
import PublicRequestForm from './pages/public/PublicRequestForm';
import RequestConfirmation from './pages/public/RequestConfirmation';
import VerifySignature from './pages/public/VerifySignature';
import SignDischarge from './pages/public/SignDischarge';

// Auth Pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import OtpVerify from './pages/auth/OtpVerify';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Requests from './pages/requests/Requests';
import RequestDetails from './pages/requests/RequestDetails';
import CreateAnimateurRequest from './pages/requests/CreateAnimateurRequest';
import CreateBusinessUnitRequest from './pages/requests/CreateBusinessUnitRequest';
import Statistics from './pages/statistics/Statistics';
import Profile from './pages/dashboard/Profile';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import ZoneManagement from './pages/zone/ZoneManagement';
import ChefDeZoneDashboard from './pages/zone/ChefDeZoneDashboard';
import AnimateurDashboard from './pages/zone/AnimateurDashboard';
import DistributeurDashboard from './pages/distributeur/DistributeurDashboard';
import ProductManagement from './pages/admin/ProductManagement';
import DistributorManagement from './pages/admin/DistributorManagement';
import InternalSiteManagement from './pages/admin/InternalSiteManagement';
import DischargeRecap from './pages/requests/DischargeRecap';
import SupplyChainReport from './pages/reports/SupplyChainReport';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  // This is a placeholder for actual authentication logic
  const isAuthenticated = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/request" element={<PublicRequestForm />} />
            <Route path="/request/confirmation/:id" element={<RequestConfirmation />} />
            <Route path="/verify/:signatureId" element={<VerifySignature />} />
            <Route path="/sign/:signToken" element={<SignDischarge />} />
            
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-otp" element={<OtpVerify />} />
            </Route>
            
            {/* Protected Routes */}
            <Route element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              {/* Un distributeur n'a qu'une file : ses décharges à signer. */}
              <Route path="/dashboard" element={
                localStorage.getItem('userRole') === 'distributeur'
                  ? <Navigate to="/distributeur/dashboard" replace />
                  : <Dashboard />
              } />
              <Route path="/requests" element={<Requests />} />
              <Route path="/requests/create-animateur" element={
                <ProtectedRoute allowedRoles={['animateur', 'chef_de_zone', 'marketing']}>
                  <CreateAnimateurRequest />
                </ProtectedRoute>
              } />
              <Route path="/requests/create-business-unit" element={
                <ProtectedRoute allowedRoles={['business_unit']}>
                  <CreateBusinessUnitRequest />
                </ProtectedRoute>
              } />
              <Route path="/requests/discharge-recap" element={
                <ProtectedRoute allowedRoles={['force_de_vente', 'chef_de_zone', 'directrice_marketing']}>
                  <DischargeRecap />
                </ProtectedRoute>
              } />
              <Route path="/reports/supply-chain" element={
                <ProtectedRoute allowedRoles={['commercial', 'directrice_marketing']}>
                  <SupplyChainReport />
                </ProtectedRoute>
              } />
              <Route path="/requests/:id" element={<RequestDetails />} />
              <Route path="/statistics" element={
                <ProtectedRoute allowedRoles={['marketing', 'force_de_vente', 'commercial', 'directrice_marketing', 'chef_de_zone', 'animateur', 'business_unit']}>
                  <Statistics />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={<Profile />} />
              
              {/* Admin Routes */}
              <Route path="/admin/users" element={
                <ProtectedRoute allowedRoles={['directrice_marketing']}>
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/products" element={
                <ProtectedRoute allowedRoles={['directrice_marketing']}>
                  <ProductManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/distributors" element={
                <ProtectedRoute allowedRoles={['directrice_marketing']}>
                  <DistributorManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/internal-sites" element={
                <ProtectedRoute allowedRoles={['directrice_marketing']}>
                  <InternalSiteManagement />
                </ProtectedRoute>
              } />

              {/* Marketing Routes */}
              <Route path="/marketing/zones" element={
                <ProtectedRoute allowedRoles={['directrice_marketing']}>
                  <ZoneManagement />
                </ProtectedRoute>
              } />

              {/* Chef de Zone Routes */}
              <Route path="/chef-de-zone/dashboard" element={
                <ProtectedRoute allowedRoles={['chef_de_zone']}>
                  <ChefDeZoneDashboard />
                </ProtectedRoute>
              } />
              {/* Distributeur Routes */}
              <Route path="/distributeur/dashboard" element={
                <ProtectedRoute allowedRoles={['distributeur']}>
                  <DistributeurDashboard />
                </ProtectedRoute>
              } />
              {/* Animateur Routes */}
              <Route path="/animateur/dashboard" element={
                <ProtectedRoute allowedRoles={['animateur']}>
                  <AnimateurDashboard />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* Redirect to dashboard if logged in, otherwise to login */}
            <Route path="/" element={
              localStorage.getItem('token') 
                ? <Navigate to="/dashboard" replace /> 
                : <Navigate to="/login" replace />
            } />
            
            {/* Catch all - 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast notifications */}
          <Toaster position="top-right" />
        </NotificationProvider>
      </AuthProvider>
    </>
  );
}

export default App;
